import type { ReactNode } from "react";
import { createContext, useState, useEffect, useContext } from "react";
import type { DomainLoanApplicationDetails } from "@porchpass/api-client";
import { useDealer } from "src/hooks/useDealer";
import { defaultApplication } from "src/constants";
import { useSafeApiCall, useApiClient } from "src/hooks/api";
import { useApplicationUpdater } from "src/hooks/useApplicationUpdater";
import { useDeleteApplicationInvite } from "src/hooks/useDeleteApplicationInvite";

interface ApplicationContextType {
  application: DomainLoanApplicationDetails;
  updateApplicationDetails: (
    fieldsToUpdate: Partial<DomainLoanApplicationDetails>,
  ) => Promise<void>;
  deleteApplicationInvite: () => void;
  isLoading: boolean;
  refetchApplication: () => Promise<void>;
}

const ApplicationContext = createContext<ApplicationContextType>({
  application: defaultApplication,
  updateApplicationDetails: () => Promise.resolve(),
  deleteApplicationInvite: () => {},
  isLoading: true,
  refetchApplication: () => Promise.resolve(),
});

export const ApplicationProvider: React.FC<{
  children: ReactNode;
  application_id: string | undefined;
}> = ({ children, application_id }) => {
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();
  const { dealer_id } = useDealer();
  const updateAppDetails = useApplicationUpdater();
  const deleteAppInvite = useDeleteApplicationInvite();

  const [application, setApplication] =
    useState<DomainLoanApplicationDetails>(defaultApplication);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchApplicationData = () => {
    setIsLoading(true);
    const path = `/dealer-portal/v1/${dealer_id}/applications/${application_id}/details`;

    return safeApiCall(() => fetchFromApi(path))
      .then((data) => {
        if (data) {
          setApplication(data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    void fetchApplicationData();
  }, [application_id, dealer_id]);

  return (
    <ApplicationContext.Provider
      value={{
        application: application,
        updateApplicationDetails: (
          fieldsToUpdate: Partial<DomainLoanApplicationDetails>,
        ) => {
          return updateAppDetails(
            fieldsToUpdate,
            dealer_id || "",
            application_id || "",
            setApplication,
            setIsLoading,
          ).then(() => {
            if (fieldsToUpdate.application_status !== undefined) {
              return fetchApplicationData();
            }
          });
        },
        deleteApplicationInvite: () => {
          void deleteAppInvite(
            dealer_id || "",
            application_id || "",
            application.application_status ?? "",
            setIsLoading,
          );
        },
        isLoading,
        refetchApplication: fetchApplicationData,
      }}
    >
      {children}
    </ApplicationContext.Provider>
  );
};

export const useApplication = () => {
  return useContext(ApplicationContext);
};
